// The sun. It used to be a 220 m cube hung eight kilometres out — the last
// crate left in a sky that had outgrown them — with a soft sprite behind it
// for the sunset. Now it is one quad turned to face the eye and drawn by a
// small shader, and it is the sun every pilot has squinted into:
//
//   * A round disc with a hard, clean limb, darker toward its edge than at
//     its heart (limb darkening — the sun is a ball of glowing gas, and you
//     see deeper into it face-on than at the rim).
//   * An aureole: the glare that hugs the disc, falling off as the square of
//     the distance from it, so the sun reads as blinding without a pixel of
//     it being bigger than it should be. At golden hour a wide, flattened
//     glow swells round it and lies along the horizon, as the old sprite did.
//   * Its colour comes from how much air the light has crossed. Overhead it
//     is a hot white with a breath of cream; as it sinks, the blue is
//     scattered out of it first (that blue is the sky) and the disc burns
//     through yellow and orange to a deep red ember at the waterline.
//   * Near the horizon it squashes a little top to bottom — refraction lifts
//     the lower limb more than the upper — and it sinks behind the sea or a
//     ridge, never through it: the quad sits at the far plane, so anything
//     drawn in front of it hides it.
//   * An overcast or a storm veils it to a pale smudge; at night it is gone.
//
// It rides with the camera, so it is always the same size in the sky and
// never drifts past the far plane however far out you fly — the cube was
// pinned to the map's centre and grew and shrank as you crossed the map.
// Both tiers draw this one sun. Tier 2 (gfx2.js) turns it up past the bloom
// threshold and switches off the scattered sky's own disc, so there is one
// sun, not two.

import * as THREE from "three";

// Angular radius of the disc (as the tangent of the half-angle). The real
// sun is 0.27°; at a 72° field of view that is a four-pixel dot, which reads
// as a star, not a sun. Twice real, and the glare does the rest.
export const SUN_RADIUS = Math.tan((0.6 * Math.PI) / 180);
// The quad's half-width in the same units (~24°): room for the sunset swell
// to fade out to nothing before the quad's edge, so no corner of it shows.
const EXTENT = 0.45;
// Limb darkening: the rim is this much dimmer than the centre (the Sun's
// is ~0.6 in visible light; a touch less keeps the edge bright on a phone).
const LIMB = 0.5;
// Optical depth of one airmass for red, green and blue. Blue scatters most
// (Rayleigh's λ^-4), red least; these land a sun that is cream at noon,
// orange a few degrees up and ember-red on the horizon.
const TAU = [0.018, 0.055, 0.13];
// How flat the disc goes on the horizon (0.18 → 82% as tall as wide).
const FLATTEN = 0.18;
// Tier 1's disc over-exposure: the middle clips white, and the limb
// darkening shows only toward the rim, where it reads.
const DISC_OVER = 1.35;
// Tier 2's boost for a sun on the horizon (see update): bright enough to
// stand out of the scattered sky's own glow, dim enough to stay orange
// through the film curve.
const HDR_LOW = 2.5;
// Colours of the wide glow round the sun: a pale warm white by day, gold at
// dusk. (The tight aureole takes the disc's own colour.)
const GLARE_DAY = new THREE.Color(0xfff2d8);
const GLARE_DUSK = new THREE.Color(0xffa860);

/**
 * Airmass toward a sun whose direction has vertical component `e` (the sine
 * of its elevation) — Kasten & Young's fit, good right down to the horizon,
 * where a flat-earth 1/sin(e) would run to infinity. Clamped half a degree
 * below the horizon: past that the sun is gone anyway.
 */
export function airmass(e) {
  const s = Math.max(-0.0087, Math.min(1, e));
  const deg = (Math.asin(s) * 180) / Math.PI;
  return 1 / (s + 0.50572 * Math.pow(deg + 6.07995, -1.6364));
}

/**
 * The disc's colour at elevation sine `e`, written into `out` (linear), and
 * how bright it burns (returned, 0..1): the light that got through each
 * channel, normalised so the brightest channel is 1 — the sun is always the
 * brightest thing in its patch of sky — with the brightness carrying the
 * dimming instead, so a setting sun is a colour you can look at.
 */
export function sunTint(e, out) {
  const m = airmass(e);
  const r = Math.exp(-TAU[0] * m), g = Math.exp(-TAU[1] * m), b = Math.exp(-TAU[2] * m);
  const top = Math.max(r, g, b);
  out.setRGB(r / top, g / top, b / top);
  return 0.35 + 0.65 * top;
}

const VERT = /* glsl */ `
uniform vec3 uDir;
uniform float uExtent;
varying vec2 vQ;
void main() {
  // Build the quad in view space round the sun's direction, one unit from
  // the eye, its "up" the world's up: the disc squashes and the dusk glow
  // spreads along the horizon, not along the edge of the screen.
  vec3 d = normalize((viewMatrix * vec4(uDir, 0.0)).xyz);
  vec3 up = (viewMatrix * vec4(0.0, 1.0, 0.0, 0.0)).xyz;
  vec3 r = cross(d, up);
  r = dot(r, r) > 1e-6 ? normalize(r) : vec3(1.0, 0.0, 0.0);
  vec3 u = cross(r, d);
  vQ = position.xy * uExtent;
  gl_Position = projectionMatrix * vec4(d + r * vQ.x + u * vQ.y, 1.0);
  gl_Position.z = gl_Position.w; // at the far plane: everything else is in front
}
`;

const FRAG = /* glsl */ `
uniform vec3 uDisc;     // the disc's colour times its brightness
uniform vec3 uHalo;     // the aureole's colour: the disc's own light, scattered
uniform vec3 uGlare;    // the golden-hour swell's colour
uniform float uR;       // disc radius (tangent units)
uniform float uExtent;
uniform float uFlat;    // 0 round .. FLATTEN on the horizon
uniform float uLimb;
uniform float uAureole; // the glare hugging the disc
uniform float uSwell;   // the wide golden-hour glow
uniform float uStretch; // how far the swell lies along the horizon
uniform float uVeil;    // overcast: 0 clear .. 1 a smudge
uniform float uVis;     // 0 below the horizon or at night .. 1
uniform float uGamma;   // the glare's falloff is tuned as a screen brightness
varying vec2 vQ;
void main() {
  // The disc: a clean limb one pixel wide, darkening toward it. Through an
  // overcast the limb goes soft, a bright blur behind the cloud.
  float rd = length(vec2(vQ.x, vQ.y / (1.0 - uFlat))) / uR;
  float soft = max(fwidth(rd), 1e-4) + 0.9 * uVeil;
  float cover = 1.0 - smoothstep(1.0 - soft, 1.0 + soft, rd);
  float mu = sqrt(max(0.0, 1.0 - rd * rd));
  float limb = 1.0 - uLimb * (1.0 - mu);
  // The glare: an inverse-square aureole off the disc's edge, and the swell.
  float rg = length(vQ);
  float a = uR / max(rg, uR);
  float aureole = a * a;
  float rs = length(vec2(vQ.x / uStretch, vQ.y));
  float swell = exp(-rs * 9.0);
  // Both fade to nothing before the quad's edge: a hard corner would put
  // the square straight back in the sky.
  float edge = 1.0 - smoothstep(0.55, 1.0, rg / uExtent);
  // The aureole is light off the disc's rim, so it starts where the disc
  // stops — laid over the disc it would bleach an ember sun to yellow.
  vec3 glare = uHalo * pow(aureole * uAureole * edge * (1.0 - cover), uGamma)
             + uGlare * pow(swell * uSwell * edge, uGamma);
  // An overcast dims the disc to a smudge in the glare round it.
  cover *= 1.0 - 0.85 * uVeil;
  vec3 col = uDisc * limb * cover + glare * (1.0 - 0.6 * uVeil);
  // Premultiplied: the disc covers the sky behind it, the glare adds light.
  gl_FragColor = vec4(col, cover) * uVis;
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/**
 * Build the sun into `scene`. Returns { mesh, hdr, update(state) }: `hdr`
 * scales the disc and its glare (1 on tier 1; gfx2.js raises it into bloom),
 * and update takes the sky's per-frame state — { dir (unit vector toward
 * the sun), dayK, duskK, gray }.
 */
export function buildSun(scene) {
  const uniforms = {
    uDir: { value: new THREE.Vector3(0, 1, 0) },
    uExtent: { value: EXTENT },
    uDisc: { value: new THREE.Color() },
    uHalo: { value: new THREE.Color() },
    uGlare: { value: new THREE.Color() },
    uR: { value: SUN_RADIUS },
    uFlat: { value: 0 },
    uLimb: { value: LIMB },
    uAureole: { value: 0 },
    uSwell: { value: 0 },
    uStretch: { value: 1 },
    uVeil: { value: 0 },
    uVis: { value: 1 },
    uGamma: { value: 2.2 },
  };
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    fog: false,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.OneMinusSrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  mesh.frustumCulled = false; // placed by its shader, not its transform
  mesh.name = "sun";
  scene.add(mesh);

  const tint = new THREE.Color();
  const sun = {
    mesh,
    hdr: 1,
    // Tier 2's scattered sky already glows round the sun; its glare steps
    // back so the two don't stack.
    glare: 1,
    // Tier 1 blends straight onto the screen, so the glare's falloff is laid
    // out in screen brightness and taken back to light (2.2) before the
    // shader encodes it — left linear, the faint outskirts of the glow come
    // out several times too bright and wash a quarter of the sky white.
    // Tier 2 blends light itself, in its HDR frame (1).
    set linear(on) {
      uniforms.uGamma.value = on ? 1 : 2.2;
    },
    update({ dir, dayK, duskK, gray }) {
      const e = dir.y;
      uniforms.uDir.value.copy(dir);
      const bright = sunTint(e, tint);
      // Tier 2's boost eases off as the sun sinks: a film curve bleaches
      // anything that bright to white, and a setting sun is an ember, not a
      // welding arc — but it must still outshine the scattered glow round it.
      const hk = Math.min(1, Math.max(0, e / 0.3));
      const hdr = this.hdr > HDR_LOW ? HDR_LOW + (this.hdr - HDR_LOW) * hk * hk * (3 - 2 * hk) : this.hdr;
      // A film curve washes the colour out of anything this bright, so tier
      // 2 deepens the tint going in; tier 1 clips at the screen's white, so
      // its disc runs over-exposed — else the halo, clipped white round it,
      // outshines a disc that limb darkening has left a shade of grey.
      if (hdr > 1) tint.setRGB(tint.r * tint.r, tint.g * tint.g, tint.b * tint.b);
      uniforms.uDisc.value.copy(tint).multiplyScalar(bright * (hdr > 1 ? hdr : DISC_OVER));
      uniforms.uHalo.value.copy(tint).multiplyScalar(bright * hdr);
      uniforms.uGlare.value.copy(GLARE_DAY).lerp(GLARE_DUSK, duskK).multiplyScalar(hdr);
      const low = 1 - Math.min(1, Math.max(0, e / 0.08));
      uniforms.uFlat.value = FLATTEN * low * low;
      uniforms.uAureole.value = 0.55 * this.glare;
      uniforms.uSwell.value = (0.1 * dayK + 0.38 * duskK) * this.glare;
      uniforms.uStretch.value = 1 + 1.4 * duskK;
      uniforms.uVeil.value = Math.min(1, gray * 1.25);
      // Sinks out over the last few degrees: the terrain and the sea hide it
      // where they reach the horizon, this where they don't (the map's edge).
      const x = Math.min(1, Math.max(0, (e + 0.05) / 0.07));
      uniforms.uVis.value = x * x * (3 - 2 * x);
      mesh.visible = uniforms.uVis.value > 0;
    },
  };
  return sun;
}
